import { apiClient, isHttpClientError } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { PageQuery, PageResp } from "./page";

// 학습 산출물 공유 링크. 받는 사람은 로그인 없이 `share_url` 을 연다. registry 는 발급 기록을
// 확인한 뒤 수명이 짧은 GCS 서명 URL 로 리다이렉트한다. 폐기(행 삭제)하면 다음 열기부터 열리지
// 않는다. 링크 원문은 발급·다시 발급 응답에만 있다. 목록은 발행 메타만 준다.

export type TrainingShareLinkTargetKind = "policy" | "checkpoint" | "raw_checkpoint";

// 공유할 대상. Exported 는 세션 체크포인트 행의 artifact_uri 로 고른다. Raw 는 Raw 목록의 파일
// 식별자로 고른다. BE 가 세션 파일 목록과 대조해 스코프를 판정한다.
export type TrainingShareLinkTarget =
  | { kind: "policy"; policy_id: string }
  | { kind: "checkpoint"; session_id: string; artifact_uri: string }
  | { kind: "raw_checkpoint"; session_id: string; run_id: string; artifact_path: string };

export type TrainingShareLinkIssuerView = {
  uuid: string;
  display_name: string;
};

export type TrainingShareLinkView = {
  uuid: string;
  issued_by: TrainingShareLinkIssuerView;
  target_kind: TrainingShareLinkTargetKind;
  training_session_id: string;
  // 발급 시점 스냅샷. 세션 이름이 바뀌어도 목록은 발급 때 이름을 표시한다.
  session_display_name: string | null;
  policy_id: string | null;
  policy_name: string | null;
  checkpoint_tag: string | null;
  // 받는 사람의 브라우저가 저장할 이름.
  filename: string;
  size_bytes: number | null;
  created_at: string;
  expires_at: string;
  // 링크가 서명 URL 로 리다이렉트된 횟수. 다운로드 완료 수가 아니다.
  access_count: number;
  last_accessed_at: string | null;
  // 보는 사람 기준 권한. 다시 발급은 발행자 본인만 한다. 폐기는 발행자 또는 admin 이 한다.
  can_reissue: boolean;
  can_revoke: boolean;
};

export type IssuedTrainingShareLinkView = {
  share_link: TrainingShareLinkView;
  share_url: string;
};

export async function createTrainingShareLink(
  target: TrainingShareLinkTarget,
): Promise<IssuedTrainingShareLinkView> {
  const response = await apiClient.post<IssuedTrainingShareLinkView>(
    apiRoutes.training.myShareLinks,
    target,
  );
  return response.data;
}

export async function reissueTrainingShareLink(
  shareLinkUuid: string,
): Promise<IssuedTrainingShareLinkView> {
  const response = await apiClient.post<IssuedTrainingShareLinkView>(
    apiRoutes.training.myShareLinkReissue(shareLinkUuid),
  );
  return response.data;
}

export async function revokeTrainingShareLink(shareLinkUuid: string): Promise<void> {
  await apiClient.delete(apiRoutes.training.shareLink(shareLinkUuid));
}

// 내가 발급한 링크. 최신순이고 만료된 링크도 포함한다.
export async function listMyTrainingShareLinks(
  query: PageQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<TrainingShareLinkView>> {
  const response = await apiClient.get<PageResp<TrainingShareLinkView>>(
    apiRoutes.training.myShareLinks,
    { params: query, signal: options.signal },
  );
  return response.data;
}

// 조직 전체 링크. admin 전용이다(그 외 403).
export async function listOrganizationTrainingShareLinks(
  query: PageQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<TrainingShareLinkView>> {
  const response = await apiClient.get<PageResp<TrainingShareLinkView>>(
    apiRoutes.training.shareLinks,
    { params: query, signal: options.signal },
  );
  return response.data;
}

// 다시 발급하거나 폐기하려던 링크가 이미 없다(예: 다른 창에서 폐기). 남의 링크도 같은 코드를
// 받는다. 그래서 권한에 따라 문구를 나누지 않는다.
export function isTrainingShareLinkNotFoundError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.share_link_not_found";
}

// 서버에 공개 origin 이 설정되지 않아 발급할 수 없다.
export function isTrainingShareLinkNotConfiguredError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.share_link_not_configured";
}
